import './styles.css';

// import CharacterCount from "@tiptap/extension-character-count";

import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { CommentTargetType, type User } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import type { JSONContent } from '@tiptap/core';
import dayjs from 'dayjs';
import { isEmpty, trim } from 'lodash';
import { ArrowUpCircle, PaperclipIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getUserMentionIds } from './get-user-mentions-ids';
import PaddockBubbleMenu from './paddock-bubble-menu';
import PdkEditorContent from './pdk-editor-content';
import { usePaddockEditor } from './use-paddock-editor';

type CommentPayload = {
  id: string;
  content: JSONContent;
  text: string;
  mentions: User['id'][];
};

export const OPTIMISTIC_COMMENT_ID_PREFIX = 'optimistic';

const AddCommentTiptap = ({ dealId }: { dealId: Deal['id'] }) => {
  const { editor } = usePaddockEditor({});

  const user = useUser();

  const resetEditor = useCallback(() => {
    editor?.commands.setContent('');
  }, [editor]);

  const addComment = useMutation({
    onMutate: async ({ comment }: { comment: CommentPayload }) => {
      const dealStoreComment = {
        id: comment.id,
        commentable_id: dealId,
        commentable_type: CommentTargetType.Deal,
        created_at: dayjs().toISOString(),
        updated_at: dayjs().toISOString(),
        content: comment.content,
        text: comment.text,
        edited: false,
        replies: [],
        user_id: user.id,
      };
      dealStore.addCommentV3({
        id: dealId,
        comment: dealStoreComment,
      });
    },
    mutationFn: async ({ comment }: { comment: CommentPayload }) => {
      if (!comment) return;

      const response = await pdkAxios
        .post(`/deals/${dealId}/comments/v3`, {
          id: comment.id,
          commentable_id: dealId,
          commentable_type: CommentTargetType.Deal,
          content: comment.content,
          text: comment.text,
          mentions: comment.mentions,
        })
        .catch((e) => {
          console.error('Error adding polymorphic comment', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      resetEditor();
    },
    onError: (error, variables) => {
      // UX Note: restore the content of the editor
      editor?.commands.setContent(variables?.comment?.content);
      dealStore.removeCommentV3({
        id: dealId,
        commentId: variables?.comment?.id,
      });

      toast({
        title: 'Error adding comment',
        description: error.message,
      });
      console.error('Error adding polymorphic comment', error, variables);
    },
  });

  const handleSubmitComment = () => {
    // TECH DEBT: handle trimming whitespace from `content`
    // this requires writing a function that remove hardBreaks
    // and texts that are empty
    // and trimming whitespace from the text
    const content = editor?.getJSON();

    const text = editor?.getText() || '';

    if (isEmpty(trim(text))) {
      return;
    }

    const mentionIds = getUserMentionIds(editor);

    const payload = {
      comment: {
        id: uuidv4(),
        content: content || {},
        text,
        mentions: mentionIds,
      },
    };

    resetEditor();
    addComment.mutate(payload);
  };

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div onClick={() => setIsExpanded(true)}>
      <Card className="p-4 flex flex-col gap-4">
        <CardHeader className="p-0">
          <CardDescription>
            <PaddockBubbleMenu editor={editor} />

            <PdkEditorContent
              editor={editor}
              onKeyDown={(e) => {
                if (
                  (e.key === 'Enter' && e.metaKey) ||
                  (e.key === 'Enter' && e.ctrlKey)
                ) {
                  e.preventDefault();
                  e.stopPropagation();

                  handleSubmitComment();
                }

                if (e.key === HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE) {
                  editor?.commands.blur();
                }
              }}
            />
          </CardDescription>
        </CardHeader>
        <CardFooter className="p-0">
          <motion.div
            className="flex flex-row w-full justify-end gap-2 h-10 items-center"
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: isExpanded ? 'auto' : 0,
              opacity: isExpanded ? 1 : 0,
            }}
            transition={{ duration: 0.3 }}
          >
            <PaperclipIcon
              className={cn(
                'h-5 w-5 cursor-pointer text-gray-500 hover:fill-gray-100',
                {},
              )}
              onClick={() => {
                toast({
                  title: 'Feature not implemented yet',
                  description:
                    "Slack us if you want the 'comments attachments' feature",
                });
              }}
            />

            <Tooltip>
              <TooltipTrigger asChild>
                <ArrowUpCircle
                  className={cn(
                    'h-5 w-5 cursor-pointer text-gray-500 hover:fill-gray-100',
                  )}
                  onClick={handleSubmitComment}
                />
              </TooltipTrigger>
              <TooltipContent className="flex flex-row gap-1 items-center">
                <p>Submit Comment</p>
                <KeyboardKeyIcon>{HOTKEYS.DEAL_SUBMIT_COMMENT}</KeyboardKeyIcon>
              </TooltipContent>
            </Tooltip>
          </motion.div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default AddCommentTiptap;
